import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Apps as AppsIcon,
} from '@mui/icons-material';
import { 
  useApplicationObservatory, 
  ApplicationObservatoryStatistics,
  ApplicationInfo,
  ApplicationFunction,
  Environment,
  Client,
  AgentClient,
  UIClient,
} from './hooks/useApplicationObservatory';
import { ObservatoryHeader } from '../../../components/observatory';
import { StatisticsPanel } from './panels/StatisticsPanel';
import { ApplicationRegistryPanel } from './panels/ApplicationRegistryPanel';
import { FunctionsPanel } from './panels/FunctionsPanel';
import { ClientsPanel } from './panels/ClientsPanel';
import { EnvironmentsPanel } from './panels/EnvironmentsPanel';
import { PerformancePanel } from './panels/PerformancePanel';
import { ActivityPanel } from './panels/ActivityPanel';
import { getBaseUrl } from '../../../framework/config/api.config';
import { apiClient } from '../../../framework/api/api.client';

export const ApplicationObservatoryView: React.FC = () => {
  const apiUrl = getBaseUrl();
  
  // WebSocket connection for real-time updates
  const { connected, snapshot, events, clearEvents, error: wsError } = useApplicationObservatory(apiUrl);

  // State for REST API data
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [statistics, setStatistics] = useState<ApplicationObservatoryStatistics | null>(null);
  const [applications, setApplications] = useState<ApplicationInfo[]>([]);
  const [functions, setFunctions] = useState<ApplicationFunction[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [clients, setClients] = useState<(AgentClient | UIClient | Client)[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);

  // View state
  const [selectedTab, setSelectedTab] = useState(0);

  // Load data from REST API
  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setApiError(null);
    
    try {
      const [statsRes, appsRes, funcsRes, envsRes, clientsRes] = await Promise.all([
        apiClient.call('/application-observatory/statistics').catch(() => ({ statistics: null })),
        apiClient.call('/application-observatory/applications').catch(() => ({ applications: [] })),
        apiClient.call('/application-observatory/functions').catch(() => ({ functions: [] })),
        apiClient.call('/application-observatory/environments').catch(() => ({ environments: [] })),
        apiClient.call('/application-observatory/clients').catch(() => ({ clients: [] })),
      ]);

      setStatistics(statsRes.statistics || generateMockStatistics());
      setApplications(appsRes.applications || []);
      setFunctions(funcsRes.functions || []);
      setEnvironments(envsRes.environments || []);
      setClients(clientsRes.clients || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading application observatory data:', error);
      setApiError('Failed to load data. Using mock data for demonstration.');
      // Set mock data for demonstration
      setStatistics(generateMockStatistics());
      setApplications(generateMockApplications());
      setFunctions(generateMockFunctions());
      setEnvironments(generateMockEnvironments());
      setClients(generateMockClients());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      loadData(true);
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  const handleRefresh = async () => {
    await loadData();
  };

  return (
    <Box sx={{ p: 3 }}>
      <ObservatoryHeader
        icon={<AppsIcon />}
        title="Application Observatory"
        subtitle="Monitor applications, functions, and client interactions"
        connected={connected}
        showConnectionStatus={true}
        eventCount={events.length}
        lastRefresh={lastRefresh}
        refreshing={refreshing}
        onRefresh={handleRefresh}
      />

      {/* Error Alert */}
      {(apiError || wsError) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {apiError || wsError} Showing demo data.
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 2 }}>
        <Tabs 
          value={selectedTab} 
          onChange={(e, newValue) => setSelectedTab(newValue)} 
          variant="scrollable" 
          scrollButtons="auto"
        >
          <Tab label="Overview" />
          <Tab label={`Applications ${applications.length > 0 ? `(${applications.length})` : ''}`} />
          <Tab label={`Functions ${functions.length > 0 ? `(${functions.length})` : ''}`} />
          <Tab label={`Clients ${clients.length > 0 ? `(${clients.length})` : ''}`} />
          <Tab label={`Environments ${environments.length > 0 ? `(${environments.length})` : ''}`} />
          <Tab label="Performance" />
          <Tab label={`Activity ${events.length > 0 ? `(${events.length})` : ''}`} />
        </Tabs>
      </Card>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Loading Application Observatory data...</Typography>
        </Box>
      ) : (
        <>
          {/* Overview Tab */}
          {selectedTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StatisticsPanel statistics={statistics} snapshot={snapshot} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ApplicationRegistryPanel applications={applications.slice(0, 5)} compact />
              </Grid>
              <Grid item xs={12} md={6}>
                <ClientsPanel clients={clients.slice(0, 5)} compact />
              </Grid>
              {events.length > 0 && (
                <Grid item xs={12}>
                  <ActivityPanel events={events.slice(0, 10)} onClearEvents={clearEvents} compact />
                </Grid>
              )}
            </Grid>
          )}

          {/* Applications Tab */}
          {selectedTab === 1 && (
            <ApplicationRegistryPanel applications={applications} />
          )}

          {/* Functions Tab */}
          {selectedTab === 2 && (
            <FunctionsPanel functions={functions} applications={applications} />
          )}

          {/* Clients Tab */}
          {selectedTab === 3 && (
            <ClientsPanel clients={clients} />
          )}

          {/* Environments Tab */}
          {selectedTab === 4 && (
            <EnvironmentsPanel environments={environments} applications={applications} />
          )}

          {/* Performance Tab */}
          {selectedTab === 5 && (
            <PerformancePanel />
          )}

          {/* Activity Tab */}
          {selectedTab === 6 && (
            <ActivityPanel events={events} onClearEvents={clearEvents} />
          )}
        </>
      )}
    </Box>
  );
};

// ============================================================================
// Mock Data Generators
// ============================================================================

function generateMockStatistics(): ApplicationObservatoryStatistics {
  return {
    totalApplications: 8,
    publishedApplications: 5,
    draftApplications: 2,
    rebuildRequired: 1,
    totalFunctions: 47,
    implementedFunctions: 38,
    unimplementedFunctions: 9,
    totalEnvironments: 3,
    healthyEnvironments: 2,
    degradedEnvironments: 1,
    totalClients: 12,
    agentClients: 5,
    uiClients: 4,
    apiClients: 3,
    totalApiCalls: 15420,
    apiCallsToday: 1248,
    apiCallsThisHour: 89,
    averageResponseTime: 245,
    errorRate: 0.3,
    mostActiveApplication: {
      id: 'app-1',
      name: 'Cake Sales Dashboard',
      callCount: 4521,
    },
    slowestFunction: {
      id: 'func-1',
      name: 'loadMultiLocationDataset',
      avgDuration: 11400,
    },
  };
}

function generateMockApplications(): ApplicationInfo[] {
  return [
    {
      id: 'app-1',
      key: 'cake-sales',
      name: 'Cake Sales Dashboard',
      description: 'Multi-location restaurant analytics dashboard',
      version: '2.1.0',
      status: 'published',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-12-01'),
    },
    {
      id: 'app-2',
      key: 'winpos-buy',
      name: 'WinPOS Buy Integration',
      description: 'Point of sale integration for ordering',
      version: '1.5.2',
      status: 'published',
      createdAt: new Date('2024-02-20'),
      updatedAt: new Date('2024-11-28'),
    },
    {
      id: 'app-3',
      key: 'inventory-mgmt',
      name: 'Inventory Management',
      description: 'Stock tracking and reorder management',
      version: '1.0.0',
      status: 'draft',
      createdAt: new Date('2024-11-01'),
      updatedAt: new Date('2024-12-03'),
    },
    {
      id: 'app-4',
      key: 'employee-scheduler',
      name: 'Employee Scheduler',
      description: 'Shift scheduling and time tracking',
      version: '3.0.1',
      status: 'published',
      createdAt: new Date('2023-08-10'),
      updatedAt: new Date('2024-10-15'),
    },
    {
      id: 'app-5',
      key: 'loyalty-program',
      name: 'Loyalty Program',
      description: 'Customer rewards and points management',
      version: '2.0.0',
      status: 'rebuild_required',
      createdAt: new Date('2024-03-05'),
      updatedAt: new Date('2024-11-20'),
    },
  ];
}

function generateMockFunctions(): ApplicationFunction[] {
  return [
    {
      id: 'func-1',
      applicationId: 'app-1',
      applicationKey: 'cake-sales',
      operationId: 'loadMultiLocationDataset',
      name: 'loadMultiLocationDataset',
      path: '/analytics/datasets/multi-location',
      method: 'GET',
      description: 'Load analytics data for all locations',
      implementationType: 'sql',
      hasImplementation: true,
    },
    {
      id: 'func-2',
      applicationId: 'app-1',
      applicationKey: 'cake-sales',
      operationId: 'getSalesMetrics',
      name: 'getSalesMetrics',
      path: '/analytics/metrics/sales',
      method: 'GET',
      description: 'Get sales KPIs and trends',
      implementationType: 'sql',
      hasImplementation: true,
    },
    {
      id: 'func-3',
      applicationId: 'app-2',
      applicationKey: 'winpos-buy',
      operationId: 'createOrder',
      name: 'createOrder',
      path: '/orders',
      method: 'POST',
      description: 'Create a new order',
      implementationType: 'integration',
      hasImplementation: true,
    },
    {
      id: 'func-4',
      applicationId: 'app-2',
      applicationKey: 'winpos-buy',
      operationId: 'getOrderStatus',
      name: 'getOrderStatus',
      path: '/orders/:id/status',
      method: 'GET',
      description: 'Get order status by ID',
      implementationType: 'integration',
      hasImplementation: true,
    },
    {
      id: 'func-5',
      applicationId: 'app-3',
      applicationKey: 'inventory-mgmt',
      operationId: 'getStockLevels',
      name: 'getStockLevels',
      path: '/inventory/stock',
      method: 'GET',
      description: 'Get current stock levels',
      implementationType: 'none',
      hasImplementation: false,
    },
  ];
}

function generateMockEnvironments(): Environment[] {
  return [
    {
      id: 'env-1',
      name: 'Production',
      key: 'production',
      type: 'production',
      url: 'https://api.commerce.example.com',
      status: 'healthy',
      applications: ['app-1', 'app-2', 'app-4'],
      lastHealthCheck: new Date(Date.now() - 30000),
    },
    {
      id: 'env-2',
      name: 'Staging',
      key: 'staging',
      type: 'staging',
      url: 'https://staging-api.commerce.example.com',
      status: 'healthy',
      applications: ['app-1', 'app-2', 'app-3', 'app-4', 'app-5'],
      lastHealthCheck: new Date(Date.now() - 60000),
    },
    {
      id: 'env-3',
      name: 'Development',
      key: 'development',
      type: 'development',
      url: 'http://localhost:5002',
      status: 'degraded',
      applications: ['app-1', 'app-2', 'app-3', 'app-4', 'app-5'],
      lastHealthCheck: new Date(Date.now() - 120000),
    },
  ];
}

function generateMockClients(): (AgentClient | UIClient | Client)[] {
  return [
    {
      id: 'client-1',
      name: 'Sales Analytics Agent',
      type: 'agent' as const,
      applicationId: 'app-1',
      applicationKey: 'cake-sales',
      description: 'AI agent for sales analytics queries',
      agentKey: 'sales-analytics-agent',
      model: 'gpt-4',
      conversationCount: 1234,
      lastConversation: new Date(Date.now() - 300000),
      capabilities: ['query', 'visualize', 'export'],
    } as AgentClient,
    {
      id: 'client-2',
      name: 'MCP Sales Dashboard',
      type: 'mcp' as const,
      applicationId: 'app-1',
      applicationKey: 'cake-sales',
      description: 'MCP integration for Cursor/Claude',
      agentKey: 'mcp-sales',
      conversationCount: 456,
      lastConversation: new Date(Date.now() - 600000),
    } as AgentClient,
    {
      id: 'client-3',
      name: 'Admin Portal',
      type: 'ui' as const,
      applicationId: 'app-1',
      applicationKey: 'cake-sales',
      description: 'Web admin dashboard',
      platform: 'web' as const,
      version: '2.1.0',
      userCount: 45,
      sessionCount: 892,
      lastSession: new Date(Date.now() - 60000),
    } as UIClient,
    {
      id: 'client-4',
      name: 'Mobile POS App',
      type: 'ui' as const,
      applicationId: 'app-2',
      applicationKey: 'winpos-buy',
      description: 'Mobile point of sale application',
      platform: 'mobile' as const,
      version: '1.5.0',
      userCount: 120,
      sessionCount: 3421,
      lastSession: new Date(Date.now() - 30000),
    } as UIClient,
    {
      id: 'client-5',
      name: 'External API Integration',
      type: 'api' as const,
      applicationId: 'app-2',
      applicationKey: 'winpos-buy',
      description: 'Third-party ordering system',
      metadata: { partnerId: 'partner-123' },
    },
  ];
}

export default ApplicationObservatoryView;

